// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { api } from '../utils/api.js';
import { saveSession } from '../utils/auth.js';
import { supabase } from '../supabaseClient';
import Logo from '../components/shared/Logo.jsx';

function getPasswordStrength(password) {
  if (!password || password.length === 0) return null;
  if (password.length < 8) return { pct: 33, color: 'var(--color-red)', label: 'Too short — minimum 8 characters' };
  if (!/\d/.test(password)) return { pct: 66, color: 'var(--color-yellow)', label: 'Fair — add a number to strengthen' };
  return { pct: 100, color: 'var(--color-green)', label: 'Strong' };
}

export default function Signup() {
  const navigate = useNavigate();
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(true);
  const location = useLocation();
  const googleAuthFailed = new URLSearchParams(location.search).get('error') === 'auth_failed';

  const emailRef = useRef(null);
  const passwordRef = useRef(null);

  useEffect(() => {
    setVisible(false);
    const t = setTimeout(() => setVisible(true), 10);
    return () => clearTimeout(t);
  }, [step]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (step === 'email') {
        emailRef.current?.focus();
      } else {
        passwordRef.current?.focus();
      }
    }, 30);
    return () => clearTimeout(t);
  }, [step]);

  const strength = getPasswordStrength(password);

  async function handleGoogleSignIn() {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin + '/auth/callback',
      },
    });
    if (error) setEmailError('Something went wrong. Please try again.');
  }

  async function handleContinue(e) {
    e.preventDefault();
    setEmailError('');
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    setLoading(true);
    try {
      const data = await api.checkEmail(email.trim());
      if (data.exists) {
        navigate(`/login?email=${encodeURIComponent(email.trim())}`);
      } else {
        setStep('password');
      }
    } catch {
      setEmailError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateAccount(e) {
    e.preventDefault();
    setPasswordError('');
    if (password.length < 8) {
      setPasswordError('Password must be at least 8 characters.');
      return;
    }
    setLoading(true);
    try {
      const data = await api.signup(email.trim(), password);
      if (data.session) {
        saveSession(data.session, data.user);
        navigate('/dashboard');
      } else {
        navigate('/login?verified=false');
      }
    } catch (err) {
      const msg = err.message || '';
      if (msg.toLowerCase().includes('already') || msg.toLowerCase().includes('registered')) {
        setPasswordError('__email_taken__');
      } else {
        setPasswordError(msg || 'Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  const fadeStyle = {
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateY(0)' : 'translateY(6px)',
    transition: 'opacity 0.15s ease-out, transform 0.15s ease-out',
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--color-bg)',
      padding: 24,
    }}>
      <div style={{
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: 14,
        boxShadow: 'var(--shadow-card)',
        padding: '40px 40px 32px',
        width: '100%',
        maxWidth: 440,
        boxSizing: 'border-box',
      }}>

        {step === 'email' ? (
          <div style={fadeStyle}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
              <Logo size={36} />
            </div>

            <h1 style={{
              fontSize: 22, fontWeight: 700, color: 'var(--color-text)',
              textAlign: 'center', letterSpacing: '-0.02em',
              margin: '0 0 4px 0',
            }}>
              Create your account
            </h1>
            <p style={{
              fontSize: 14, fontWeight: 400, color: 'var(--color-text-2)',
              textAlign: 'center', margin: '0 0 28px 0',
            }}>
              Start converting your first catalog — free, no card required.
            </p>

            <button
              onClick={handleGoogleSignIn}
              style={{
                width: '100%',
                padding: '11px 16px',
                border: '1px solid var(--color-border-strong)',
                borderRadius: 10,
                background: 'var(--color-surface)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                fontSize: 14,
                fontWeight: 600,
                color: 'var(--color-text)',
                transition: 'background 0.15s, border-color 0.15s',
                marginBottom: googleAuthFailed ? 8 : 20,
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--color-surface-2)'}
              onMouseLeave={e => e.currentTarget.style.background = 'var(--color-surface)'}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908C16.658 14.205 17.64 11.892 17.64 9.2z" fill="#4285F4"/>
                <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
                <path d="M3.964 10.706A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.038l3.007-2.332z" fill="#FBBC05"/>
                <path d="M9 3.576c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.576 9 3.576z" fill="#EA4335"/>
              </svg>
              Continue with Google
            </button>
            {googleAuthFailed && (
              <p style={{ fontSize: 12, color: 'var(--color-red-text)', margin: '0 0 20px 0' }}>
                Google sign-in failed. Please try again or use email below.
              </p>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <div style={{ flex: 1, height: 1, background: 'var(--color-border)' }} />
              <span style={{ fontSize: 12, color: 'var(--color-text-3)', whiteSpace: 'nowrap' }}>
                or continue with email
              </span>
              <div style={{ flex: 1, height: 1, background: 'var(--color-border)' }} />
            </div>

            <form onSubmit={handleContinue} noValidate>
              <label style={{
                fontSize: 12, fontWeight: 600, color: 'var(--color-text-2)',
                display: 'block', marginBottom: 6,
              }}>
                Email address
              </label>
              <input
                ref={emailRef}
                type="email"
                placeholder="you@yourstore.com"
                value={email}
                autoComplete="email"
                onChange={e => { setEmail(e.target.value); if (emailError) setEmailError(''); }}
                style={{
                  width: '100%',
                  padding: '11px 14px',
                  border: `1px solid ${emailError ? 'var(--color-red)' : 'var(--color-border)'}`,
                  borderRadius: 8,
                  fontSize: 14,
                  color: 'var(--color-text)',
                  background: 'var(--color-surface)',
                  outline: 'none',
                  transition: 'border-color 0.15s',
                  marginBottom: emailError ? 0 : 16,
                  boxSizing: 'border-box',
                  display: 'block',
                }}
                onFocus={e => { if (!emailError) e.target.style.borderColor = 'var(--color-primary)'; }}
                onBlur={e => { if (!emailError) e.target.style.borderColor = 'var(--color-border)'; }}
              />
              {emailError && (
                <p style={{ fontSize: 12, color: 'var(--color-red-text)', margin: '6px 0 16px 0' }}>
                  {emailError}
                </p>
              )}

              <button
                type="submit"
                style={{
                  width: '100%',
                  padding: '12px 20px',
                  background: loading ? 'var(--color-text-3)' : 'var(--color-primary)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 10,
                  fontSize: 15,
                  fontWeight: 700,
                  cursor: loading ? 'default' : 'pointer',
                  letterSpacing: '-0.01em',
                  transition: 'background 0.15s',
                  marginBottom: 20,
                  pointerEvents: loading ? 'none' : 'auto',
                }}
                onMouseEnter={e => { if (!loading) e.currentTarget.style.background = 'var(--color-primary-hover)'; }}
                onMouseLeave={e => { if (!loading) e.currentTarget.style.background = 'var(--color-primary)'; }}
              >
                {loading ? 'Checking...' : 'Continue →'}
              </button>
            </form>

            <p style={{ fontSize: 13, color: 'var(--color-text-3)', textAlign: 'center', margin: '0 0 24px 0' }}>
              Already have an account?{' '}
              <Link
                to="/login"
                style={{ color: 'var(--color-primary)', fontWeight: 500, textDecoration: 'none' }}
                onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
              >
                Sign in
              </Link>
            </p>

            <p style={{ fontSize: 11, color: 'var(--color-text-3)', textAlign: 'center', margin: 0, lineHeight: 1.6 }}>
              By continuing, you agree to our{' '}
              <a
                href="/terms"
                style={{ color: 'var(--color-text-2)', textDecoration: 'none' }}
                onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
              >
                Terms of Service
              </a>
              {' '}and{' '}
              <a
                href="/privacy"
                style={{ color: 'var(--color-text-2)', textDecoration: 'none' }}
                onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
              >
                Privacy Policy
              </a>.
            </p>
          </div>

        ) : (
          <div style={{ ...fadeStyle, display: 'flex', flexDirection: 'column' }}>
            <button
              onClick={() => setStep('email')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: 13,
                color: 'var(--color-text-2)',
                padding: '0 0 20px 0',
                alignSelf: 'flex-start',
              }}
            >
              ← Back
            </button>

            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
              <Logo size={36} />
            </div>

            <h1 style={{
              fontSize: 22, fontWeight: 700, color: 'var(--color-text)',
              textAlign: 'center', letterSpacing: '-0.02em',
              margin: '0 0 4px 0',
            }}>
              Set your password
            </h1>
            <p style={{
              fontSize: 13, color: 'var(--color-text-2)',
              textAlign: 'center', margin: '0 0 28px 0',
            }}>
              Creating account for:{' '}
              <span style={{ fontWeight: 600, color: 'var(--color-text)' }}>{email}</span>
            </p>

            <form onSubmit={handleCreateAccount} noValidate>
              <label style={{
                fontSize: 12, fontWeight: 600, color: 'var(--color-text-2)',
                display: 'block', marginBottom: 6,
              }}>
                Password
              </label>
              <input
                ref={passwordRef}
                type="password"
                placeholder="At least 8 characters"
                value={password}
                autoComplete="new-password"
                onChange={e => { setPassword(e.target.value); if (passwordError) setPasswordError(''); }}
                style={{
                  width: '100%',
                  padding: '11px 14px',
                  border: `1px solid ${passwordError ? 'var(--color-red)' : 'var(--color-border)'}`,
                  borderRadius: 8,
                  fontSize: 14,
                  color: 'var(--color-text)',
                  background: 'var(--color-surface)',
                  outline: 'none',
                  transition: 'border-color 0.15s',
                  marginBottom: 6,
                  boxSizing: 'border-box',
                  display: 'block',
                }}
                onFocus={e => { if (!passwordError) e.target.style.borderColor = 'var(--color-primary)'; }}
                onBlur={e => { if (!passwordError) e.target.style.borderColor = 'var(--color-border)'; }}
              />

              {password.length > 0 && strength && (
                <div style={{ marginBottom: 4 }}>
                  <div style={{
                    height: 4, borderRadius: 999,
                    background: 'var(--color-border)',
                    overflow: 'hidden', marginBottom: 4,
                  }}>
                    <div style={{
                      height: '100%',
                      borderRadius: 999,
                      width: `${strength.pct}%`,
                      background: strength.color,
                      transition: 'width 0.3s ease, background 0.3s ease',
                    }} />
                  </div>
                  <span style={{ fontSize: 11, color: strength.color }}>{strength.label}</span>
                </div>
              )}

              {passwordError === '__email_taken__' ? (
                <p style={{ fontSize: 12, color: 'var(--color-red-text)', margin: '10px 0 0 0' }}>
                  An account with this email already exists.{' '}
                  <Link
                    to={`/login?email=${encodeURIComponent(email)}`}
                    style={{ color: 'var(--color-red-text)', fontWeight: 600 }}
                  >
                    Sign in instead
                  </Link>
                </p>
              ) : passwordError ? (
                <p style={{ fontSize: 12, color: 'var(--color-red-text)', margin: '10px 0 0 0' }}>
                  {passwordError}
                </p>
              ) : null}

              <button
                type="submit"
                style={{
                  width: '100%',
                  padding: '12px 20px',
                  background: loading ? 'var(--color-text-3)' : 'var(--color-primary)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 10,
                  fontSize: 15,
                  fontWeight: 700,
                  cursor: loading ? 'default' : 'pointer',
                  letterSpacing: '-0.01em',
                  transition: 'background 0.15s',
                  marginTop: 20,
                  pointerEvents: loading ? 'none' : 'auto',
                  display: 'block',
                }}
                onMouseEnter={e => { if (!loading) e.currentTarget.style.background = 'var(--color-primary-hover)'; }}
                onMouseLeave={e => { if (!loading) e.currentTarget.style.background = 'var(--color-primary)'; }}
              >
                {loading ? 'Creating account...' : 'Create my account →'}
              </button>
            </form>

            <p style={{ fontSize: 11, color: 'var(--color-text-3)', textAlign: 'center', margin: '24px 0 0', lineHeight: 1.6 }}>
              By continuing, you agree to our{' '}
              <a
                href="/terms"
                style={{ color: 'var(--color-text-2)', textDecoration: 'none' }}
                onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
              >
                Terms of Service
              </a>
              {' '}and{' '}
              <a
                href="/privacy"
                style={{ color: 'var(--color-text-2)', textDecoration: 'none' }}
                onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
              >
                Privacy Policy
              </a>.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
